import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../../i18n/LanguageProvider";
import { scrollToId } from "./SmoothScroll";

const IMGS = {
  ES: "https://images.unsplash.com/photo-1509840841025-9088ba78a826?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  MA: "https://images.unsplash.com/photo-1489749798305-4fea3ae63d43?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  GN: "https://images.unsplash.com/photo-1580060839134-75a5edca2e99?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  FR: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  IT: "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  TR: "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  AE: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  SA: "https://images.unsplash.com/photo-1579305796288-c534f6cf17ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

/**
 * Editorial bento layout: 7 destinations, uneven grid with a large hero card.
 */
const layoutClasses = [
  "md:col-span-6 md:row-span-2 aspect-[4/5]", // ES — big feature
  "md:col-span-3 aspect-square",              // MA
  "md:col-span-3 aspect-square",              // GN
  "md:col-span-3 aspect-[4/5]",               // FR
  "md:col-span-3 aspect-[4/5]",               // IT
  "md:col-span-4 aspect-[4/3]",               // TR
  "md:col-span-4 aspect-[4/3]",               // AE
  "md:col-span-4 aspect-[4/3]",               // SA — Kaaba
];

export const Destinations = () => {
  const { t } = useLang();

  return (
    <section
      id="destinations"
      data-testid="destinations-section"
      className="relative bg-[#F5F1E8] text-[#0B1416] py-28 md:py-40"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 md:mb-24"
        >
          <div>
            <div className="overline text-[#0D7A7D]">— {t.destinations.eyebrow}</div>
            <h2
              data-testid="destinations-title"
              className="font-heading-tight text-5xl md:text-6xl lg:text-7xl leading-[0.95] mt-6 max-w-3xl"
            >
              {t.destinations.title}
            </h2>
          </div>
          <button
            data-testid="destinations-cta"
            onClick={() => scrollToId("booking")}
            className="tt-btn tt-btn-dark shrink-0"
          >
            {t.hero.quote}
            <ArrowUpRight size={14} />
          </button>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {t.destinations.list.map((d, i) => (
            <motion.div
              key={d.code}
              data-testid={`destination-${d.code}`}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              custom={i}
              className={`clip-frame relative bg-[#0B1416] ${layoutClasses[i]}`}
            >
              <img
                src={IMGS[d.code]}
                alt={d.name}
                className="w-full h-full object-cover opacity-90"
                loading="lazy"
              />
              {/* Bottom gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1416]/90 via-[#0B1416]/10 to-transparent pointer-events-none" />
              <div className="absolute top-4 left-4 flag-pill bg-[#F5F1E8]/12 backdrop-blur-md text-[#F5F1E8]">
                <span className="text-lg mr-2">{d.flag}</span>
                {d.code}
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-[#F5F1E8]">
                <div className="font-heading text-3xl md:text-4xl leading-none">{d.name}</div>
                <div className="text-[11px] uppercase tracking-[0.28em] mt-3 opacity-75">
                  {d.tag}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
